import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import Swal from 'sweetalert2'

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
  purple: '#7C3AED',
}

const CATEGORY_META = {
  data:      { label: 'Patient / Transactional', short: 'Data',      bg: '#e0f2fe', color: '#075985' },
  reference: { label: 'Reference / Catalogue',   short: 'Reference', bg: '#fef9c3', color: '#854d0e' },
  system:    { label: 'System / Auth',           short: 'System',    bg: '#fee2e2', color: '#991b1b' },
  locked:    { label: 'Protected',               short: 'Locked',    bg: '#e8ecf0', color: '#5B6B79' },
}

const CONFIRM_WORD = 'WIPE'

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 12px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 12, color: '#2d3748', padding: '7px 12px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}
const inputStyle = {
  height: 32, borderRadius: 7, border: '1px solid #dde3eb',
  padding: '0 10px', fontSize: 12, color: '#2d3748', outline: 'none',
}

const Badge = ({ label, bg, color, title }) => (
  <span title={title} style={{
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
    background: bg || '#e8ecf0', color: color || COLORS.gray, whiteSpace: 'nowrap',
  }}>{label}</span>
)

const formatBytes = (n) => {
  if (!n) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

const errMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback

const DatabaseCleanup = () => {
  const navigate = useNavigate()

  // ── who is logged in ──
  const isAdmin = useMemo(() => {
    try {
      const raw = localStorage.getItem('userMedical')
      return !!raw && String(JSON.parse(raw)?.data?.role || '').toLowerCase() === 'admin'
    } catch {
      return false
    }
  }, [])

  // ── step 1: password gate ──
  const [password, setPassword]   = useState('')
  const [showPass, setShowPass]   = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [gateError, setGateError] = useState('')

  // the ticket lives in memory only — never persisted, so a reload re-asks
  const [ticket, setTicket]       = useState(null)
  const [expiresAt, setExpiresAt] = useState(null)
  const [secsLeft, setSecsLeft]   = useState(0)

  // ── step 2: tables ──
  const [loading, setLoading]   = useState(false)
  const [database, setDatabase] = useState('')
  const [tables, setTables]     = useState([])
  const [selected, setSelected] = useState(() => new Set())
  const [search, setSearch]     = useState('')
  const [catFilter, setCatFilter]     = useState('all')
  const [onlyWithRows, setOnlyWithRows] = useState(false)
  const [resetAutoInc, setResetAutoInc] = useState(true)

  // ── confirm modal + results ──
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [confirmSystem, setConfirmSystem] = useState(false)
  const [running, setRunning] = useState(false)
  const [results, setResults] = useState(null)

  const passRef = useRef(null)

  useEffect(() => {
    if (!ticket && isAdmin) passRef.current?.focus()
  }, [ticket, isAdmin])

  // ── ticket countdown ──
  useEffect(() => {
    if (!expiresAt) return
    const tick = () => {
      const left = Math.max(0, Math.round((expiresAt - Date.now()) / 1000))
      setSecsLeft(left)
      if (left === 0) resetGate('Confirmation expired — please re-enter your password')
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt])

  const resetGate = (message) => {
    setTicket(null)
    setExpiresAt(null)
    setTables([])
    setSelected(new Set())
    setResults(null)
    setConfirmOpen(false)
    setPassword('')
    setGateError(message || '')
  }

  const authHeaders = useCallback(
    () => ({ headers: { 'x-db-maintenance-ticket': ticket } }),
    [ticket]
  )

  const handleAuthFailure = (error) => {
    const code = error?.response?.data?.code
    if (error?.response?.status === 401 && code && code.startsWith('TICKET')) {
      resetGate(errMessage(error, 'Please re-enter your password'))
      return true
    }
    return false
  }

  // ── verify password ──
  const verifyPassword = async (e) => {
    e?.preventDefault()
    if (!password.trim()) return setGateError('Enter your password')
    setVerifying(true)
    setGateError('')
    try {
      const res = await axiosInstance.post('/dbMaintenance/verifyAdminPassword', { password })
      if (!res.data?.success || !res.data?.ticket) {
        throw new Error(res.data?.message || 'Verification failed')
      }
      setTicket(res.data.ticket)
      setExpiresAt(Date.now() + (res.data.expiresIn || 900) * 1000)
      setPassword('')
    } catch (error) {
      setGateError(errMessage(error, 'Could not verify password'))
    } finally {
      setVerifying(false)
    }
  }

  // ── load tables once the ticket exists ──
  const loadTables = useCallback(async () => {
    if (!ticket) return
    setLoading(true)
    try {
      const res = await axiosInstance.get('/dbMaintenance/tables', {
        headers: { 'x-db-maintenance-ticket': ticket },
      })
      setDatabase(res.data?.database || '')
      setTables(res.data?.data || [])
      setSelected(new Set())
    } catch (error) {
      if (!handleAuthFailure(error)) {
        Swal.fire({ icon: 'error', title: 'Could not load tables', text: errMessage(error, 'Unknown error') })
      }
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket])

  useEffect(() => { loadTables() }, [loadTables])

  // ── derived ──
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return tables.filter((t) => {
      if (q && !t.name.toLowerCase().includes(q)) return false
      if (catFilter !== 'all' && t.category !== catFilter) return false
      if (onlyWithRows && !(t.rows > 0)) return false
      return true
    })
  }, [tables, search, catFilter, onlyWithRows])

  const byName = useMemo(() => {
    const map = {}
    tables.forEach((t) => { map[t.name] = t })
    return map
  }, [tables])

  const selectedList = useMemo(() => [...selected], [selected])

  const selectedRows = useMemo(
    () => selectedList.reduce((sum, n) => sum + (byName[n]?.rows || 0), 0),
    [selectedList, byName]
  )

  const selectedSystem = useMemo(
    () => selectedList.filter((n) => byName[n]?.category === 'system'),
    [selectedList, byName]
  )

  // Children that keep rows pointing at a parent about to be emptied.
  const orphanWarnings = useMemo(() => {
    const out = []
    selectedList.forEach((name) => {
      (byName[name]?.referenced_by || []).forEach((child) => {
        if (selected.has(child.table)) return
        if (child.table === name) return
        if (!(byName[child.table]?.rows > 0)) return
        if (out.some((o) => o.child === child.table && o.parent === name)) return
        out.push({ child: child.table, parent: name, column: child.column, rule: child.delete_rule })
      })
    })
    return out
  }, [selectedList, selected, byName])

  const counts = useMemo(() => {
    const c = { all: tables.length, data: 0, reference: 0, system: 0, locked: 0 }
    tables.forEach((t) => { c[t.category] = (c[t.category] || 0) + 1 })
    return c
  }, [tables])

  // ── selection helpers ──
  const toggle = (name) => {
    if (byName[name]?.locked) return
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })
  }

  const selectableVisible = visible.filter((t) => !t.locked)
  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every((t) => selected.has(t.name))

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) selectableVisible.forEach((t) => next.delete(t.name))
      else selectableVisible.forEach((t) => next.add(t.name))
      return next
    })
  }

  const selectPreset = (preset) => {
    setSelected(() => {
      const next = new Set()
      tables.forEach((t) => {
        if (t.locked) return
        if (preset === 'patient_data' && t.category === 'data') next.add(t.name)
        if (preset === 'with_rows' && t.rows > 0 && t.category !== 'system') next.add(t.name)
      })
      return next
    })
  }

  // ── run ──
  const openConfirm = () => {
    if (!selected.size) {
      return Swal.fire({ icon: 'info', title: 'Nothing selected', text: 'Pick at least one table to wipe.' })
    }
    setConfirmText('')
    setConfirmSystem(false)
    setResults(null)
    setConfirmOpen(true)
  }

  const runTruncate = async () => {
    if (confirmText.trim().toUpperCase() !== CONFIRM_WORD) return
    if (selectedSystem.length && !confirmSystem) return

    setRunning(true)
    try {
      const res = await axiosInstance.post('/dbMaintenance/truncate', {
        tables: selectedList,
        resetAutoIncrement: resetAutoInc,
        confirmSystem: selectedSystem.length > 0 ? confirmSystem : false,
      }, authHeaders())

      setConfirmOpen(false)
      setResults(res.data)

      const failed = (res.data?.results || []).filter((r) => r.status === 'failed')
      if (failed.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Finished with errors',
          html: `${(res.data.results.length - failed.length)} table(s) wiped, `
            + `<b>${failed.length}</b> failed. See the report below.`,
        })
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Database cleaned',
          text: res.data?.message || 'Selected tables were wiped.',
        })
      }

      // whatever survived, the counts on screen are stale now
      await loadTables()
    } catch (error) {
      if (handleAuthFailure(error)) return
      const data = error?.response?.data
      if (data?.code === 'SYSTEM_CONFIRMATION_REQUIRED') {
        Swal.fire({
          icon: 'warning',
          title: 'System tables selected',
          html: `Tick the system-tables confirmation first:<br/><b>${(data.tables || []).join(', ')}</b>`,
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Wipe failed',
          html: data?.tables?.length
            ? `${data.message}<br/><b>${data.tables.join(', ')}</b>`
            : errMessage(error, 'Unknown error'),
        })
      }
    } finally {
      setRunning(false)
    }
  }

  // ── access denied ──
  if (!isAdmin) {
    return (
      <div className="pc-container">
        <div className="pc-content">
          <div style={{
            background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
            padding: '40px 24px', textAlign: 'center',
          }}>
            <i className="ti ti-lock" style={{ fontSize: 34, color: COLORS.red, opacity: 0.7 }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c', marginTop: 10 }}>
              Admin access only
            </div>
            <div style={{ fontSize: 12, color: COLORS.gray, marginTop: 4 }}>
              Database cleanup is restricted to admin users.
            </div>
            <button onClick={() => navigate('/Admin/setting')} className="bgBtn btn-sm"
              style={{ marginTop: 16, fontSize: 12, padding: '6px 14px' }}>
              Back to Settings
            </button>
          </div>
        </div>
      </div>
    )
  }

  const mm = String(Math.floor(secsLeft / 60)).padStart(2, '0')
  const ss = String(secsLeft % 60).padStart(2, '0')

  return (
    <div className="pc-container">
      <div className="pc-content">

        {/* ── Header ── */}
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
          padding: '16px 24px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, background: `${COLORS.red}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="ti ti-database-x" style={{ color: COLORS.red, fontSize: 20 }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a202c' }}>Database Cleanup</div>
            <div style={{ fontSize: 12, color: COLORS.gray }}>
              Wipe selected tables to hand this instance over with a clean database
              {database ? ` — connected to ${database}` : ''}
            </div>
          </div>
          {ticket && (
            <Badge label={`Session ${mm}:${ss}`} bg="#e0f2fe" color="#075985"
              title="Your password confirmation expires after this countdown" />
          )}
          <button onClick={() => navigate('/Admin/setting')}
            style={{
              height: 32, padding: '0 12px', fontSize: 12, borderRadius: 7,
              border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            }}>
            <i className="ti ti-arrow-left" style={{ fontSize: 13 }} /> Back
          </button>
        </div>

        {/* ── Step 1: password gate ── */}
        {!ticket ? (
          <div style={{
            background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
            padding: '28px 24px', maxWidth: 520, margin: '0 auto',
          }}>
            <div style={{
              background: '#fff7ed', border: `1px solid ${COLORS.orange}55`, borderRadius: 8,
              padding: '10px 12px', fontSize: 12, color: '#92400e', marginBottom: 18,
              display: 'flex', gap: 8,
            }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
              <div>
                The next screen permanently empties tables. Wiped rows cannot be recovered —
                take a database backup first.
              </div>
            </div>

            <form onSubmit={verifyPassword}>
              <label style={{
                fontSize: 10, fontWeight: 700, color: COLORS.gray,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                Confirm your admin password
              </label>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input
                  ref={passRef}
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => { setPassword(e.target.value); setGateError('') }}
                  placeholder="Admin password"
                  style={{ ...inputStyle, flex: 1, height: 36 }}
                />
                <button type="button" onClick={() => setShowPass((v) => !v)}
                  title={showPass ? 'Hide password' : 'Show password'}
                  style={{
                    width: 36, height: 36, borderRadius: 7, border: '1px solid #dde3eb',
                    background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  }}>
                  <i className={showPass ? 'ti ti-eye-off' : 'ti ti-eye'} style={{ fontSize: 15 }} />
                </button>
              </div>

              {gateError && (
                <div style={{ fontSize: 12, color: COLORS.red, marginTop: 8, display: 'flex', gap: 5 }}>
                  <i className="ti ti-alert-circle" style={{ fontSize: 14 }} /> {gateError}
                </div>
              )}

              <button type="submit" disabled={verifying}
                style={{
                  marginTop: 16, width: '100%', height: 38, borderRadius: 8, border: 'none',
                  background: COLORS.red, color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: verifying ? 'wait' : 'pointer', opacity: verifying ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                {verifying
                  ? <><span className="spinner-border spinner-border-sm" /> Verifying…</>
                  : <><i className="ti ti-lock-open" style={{ fontSize: 15 }} /> Continue</>}
              </button>
            </form>
          </div>
        ) : (
          <>
            {/* ── Step 2: toolbar ── */}
            <div style={{
              background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
              padding: '14px 20px', marginBottom: 14,
            }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search table name…"
                  style={{ ...inputStyle, width: 220 }}
                />
                <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
                  style={{ ...inputStyle, width: 190 }}>
                  <option value="all">All categories ({counts.all})</option>
                  <option value="data">Patient / Transactional ({counts.data || 0})</option>
                  <option value="reference">Reference / Catalogue ({counts.reference || 0})</option>
                  <option value="system">System / Auth ({counts.system || 0})</option>
                  <option value="locked">Protected ({counts.locked || 0})</option>
                </select>
                <label style={{ fontSize: 12, color: COLORS.gray, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={onlyWithRows}
                    onChange={(e) => setOnlyWithRows(e.target.checked)} />
                  Only tables with rows
                </label>

                <div style={{ flex: 1 }} />

                <button onClick={() => selectPreset('patient_data')}
                  style={{
                    height: 32, padding: '0 10px', fontSize: 12, borderRadius: 7,
                    border: `1px solid ${COLORS.blue}`, background: '#fff', color: '#075985', cursor: 'pointer',
                  }}>
                  Select patient data
                </button>
                <button onClick={() => selectPreset('with_rows')}
                  style={{
                    height: 32, padding: '0 10px', fontSize: 12, borderRadius: 7,
                    border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  }}>
                  Select all non-system with rows
                </button>
                <button onClick={() => setSelected(new Set())}
                  style={{
                    height: 32, padding: '0 10px', fontSize: 12, borderRadius: 7,
                    border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  }}>
                  Clear
                </button>
                <button onClick={loadTables} disabled={loading} title="Reload table list and row counts"
                  style={{
                    height: 32, padding: '0 10px', fontSize: 12, borderRadius: 7,
                    border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  }}>
                  <i className="ti ti-refresh" style={{ fontSize: 13 }} />
                </button>
              </div>
            </div>

            {/* ── Table list ── */}
            <div style={{
              background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
              padding: '14px 20px', marginBottom: 14,
            }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: COLORS.gray }}>
                  <div className="spinner-border" role="status"
                    style={{ color: COLORS.red, width: 28, height: 28, borderWidth: 3 }} />
                  <div style={{ marginTop: 10, fontSize: 13 }}>Reading schema and counting rows…</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0', maxHeight: '58vh' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.red}`, width: 38 }}>
                          <input type="checkbox" checked={allVisibleSelected}
                            onChange={toggleAllVisible}
                            title="Select every table currently listed" />
                        </th>
                        <th style={thStyle}>Table</th>
                        <th style={thStyle}>Category</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Rows</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Size</th>
                        <th style={thStyle}>Engine</th>
                        <th style={thStyle}>Foreign keys</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.length === 0 ? (
                        <tr>
                          <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', padding: '36px 0', color: COLORS.gray }}>
                            <i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                            No tables match the filters
                          </td>
                        </tr>
                      ) : visible.map((t, i) => {
                        const meta     = CATEGORY_META[t.category] || CATEGORY_META.data
                        const isSel    = selected.has(t.name)
                        const rowBg    = isSel ? `${COLORS.red}0d` : (i % 2 === 0 ? '#fff' : '#fafbfc')
                        const children = t.referenced_by || []
                        const parents  = t.references || []
                        return (
                          <tr key={t.name} style={{ background: rowBg, opacity: t.locked ? 0.55 : 1 }}>
                            <td style={tdStyle}>
                              <input type="checkbox" checked={isSel} disabled={t.locked}
                                onChange={() => toggle(t.name)}
                                title={t.locked ? 'Protected — cannot be wiped' : ''} />
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 600, fontFamily: 'monospace' }}>
                              {t.name}
                              {t.locked && (
                                <i className="ti ti-lock" title="Protected — migration ledger"
                                  style={{ fontSize: 12, marginLeft: 6, color: COLORS.gray }} />
                              )}
                            </td>
                            <td style={tdStyle}>
                              <Badge label={meta.short} bg={meta.bg} color={meta.color} title={meta.label} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {t.rows === null ? '—' : t.rows.toLocaleString()}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: COLORS.gray }}>
                              {formatBytes(t.size_bytes)}
                            </td>
                            <td style={{ ...tdStyle, color: COLORS.gray }}>{t.engine || '—'}</td>
                            <td style={{ ...tdStyle, fontSize: 11, color: COLORS.gray }}>
                              {children.length === 0 && parents.length === 0 ? '—' : (
                                <>
                                  {children.length > 0 && (
                                    <div title={children.map((c) => `${c.table}.${c.column}`).join(', ')}>
                                      <i className="ti ti-arrow-down-left" /> referenced by {children.length}
                                    </div>
                                  )}
                                  {parents.length > 0 && (
                                    <div title={parents.map((p) => p.table).join(', ')}>
                                      <i className="ti ti-arrow-up-right" /> depends on {parents.length}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Selection summary + run ── */}
            <div style={{
              background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
              padding: '14px 20px', marginBottom: 20,
            }}>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ fontSize: 13, color: '#1a202c' }}>
                  <b>{selected.size}</b> table(s) selected
                  <span style={{ color: COLORS.gray }}> · {selectedRows.toLocaleString()} row(s) will be deleted</span>
                </div>
                <label style={{ fontSize: 12, color: COLORS.gray, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={resetAutoInc}
                    onChange={(e) => setResetAutoInc(e.target.checked)} />
                  Reset ID counters to 1
                </label>
                <div style={{ flex: 1 }} />
                <button onClick={openConfirm} disabled={!selected.size}
                  style={{
                    height: 36, padding: '0 16px', fontSize: 13, fontWeight: 600, borderRadius: 8,
                    border: 'none', background: selected.size ? COLORS.red : '#cbd5e0', color: '#fff',
                    cursor: selected.size ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  <i className="ti ti-trash" style={{ fontSize: 15 }} /> Wipe selected tables
                </button>
              </div>

              {selectedSystem.length > 0 && (
                <div style={{
                  marginTop: 12, background: '#fee2e2', border: `1px solid ${COLORS.red}44`,
                  borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#991b1b',
                }}>
                  <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />
                  <b>System tables selected:</b> {selectedSystem.join(', ')} — wiping these can remove
                  your own login and clinic configuration.
                </div>
              )}

              {orphanWarnings.length > 0 && (
                <div style={{
                  marginTop: 12, background: '#fff7ed', border: `1px solid ${COLORS.orange}55`,
                  borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#92400e',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    <i className="ti ti-link-off" style={{ marginRight: 6 }} />
                    Orphaned rows will be left behind ({orphanWarnings.length})
                  </div>
                  <div style={{ lineHeight: 1.6 }}>
                    {orphanWarnings.slice(0, 12).map((o) => (
                      <div key={`${o.child}-${o.parent}`}>
                        <code>{o.child}.{o.column}</code> points at <code>{o.parent}</code>, which
                        you are emptying — select <code>{o.child}</code> too, or its rows will
                        reference missing records.
                      </div>
                    ))}
                    {orphanWarnings.length > 12 && <div>…and {orphanWarnings.length - 12} more.</div>}
                  </div>
                </div>
              )}
            </div>

            {/* ── Report ── */}
            {results && (
              <div style={{
                background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
                padding: '14px 20px', marginBottom: 20,
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c', marginBottom: 10 }}>
                  Cleanup report
                  <span style={{ fontSize: 12, fontWeight: 400, color: COLORS.gray, marginLeft: 8 }}>
                    {results.message}
                  </span>
                </div>
                <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Table</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Method</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Rows removed</th>
                        <th style={thStyle}>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(results.results || []).map((r, i) => (
                        <tr key={`${r.table}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.table || '—'}</td>
                          <td style={tdStyle}>
                            {r.status === 'success'
                              ? <Badge label="WIPED"  bg="#dcfce7" color="#166534" />
                              : <Badge label="FAILED" bg="#fee2e2" color="#991b1b" />}
                          </td>
                          <td style={{ ...tdStyle, color: COLORS.gray }}>{r.method || '—'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {r.rows_deleted === null || r.rows_deleted === undefined
                              ? '—' : r.rows_deleted.toLocaleString()}
                          </td>
                          <td style={{ ...tdStyle, color: r.status === 'failed' ? COLORS.red : COLORS.gray }}>
                            {r.error || r.note || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Confirm modal ── */}
        {confirmOpen && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1055,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}>
            <div style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560,
              maxHeight: '86vh', overflowY: 'auto', padding: '20px 22px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 9, background: `${COLORS.red}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="ti ti-alert-triangle" style={{ color: COLORS.red, fontSize: 18 }} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c' }}>
                  Permanently empty {selected.size} table(s)?
                </div>
              </div>

              <div style={{ fontSize: 12, color: COLORS.gray, marginBottom: 10 }}>
                {selectedRows.toLocaleString()} row(s) will be deleted from <b>{database}</b>.
                This cannot be undone or rolled back — <code>TRUNCATE</code> commits immediately.
              </div>

              <div style={{
                border: '1px solid #e8ecf0', borderRadius: 8, padding: '8px 10px',
                maxHeight: 150, overflowY: 'auto', fontSize: 11, fontFamily: 'monospace',
                color: '#2d3748', marginBottom: 12, lineHeight: 1.7,
              }}>
                {[...selectedList].sort().join(', ')}
              </div>

              {selectedSystem.length > 0 && (
                <label style={{
                  display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer',
                  background: '#fee2e2', border: `1px solid ${COLORS.red}44`, borderRadius: 8,
                  padding: '10px 12px', fontSize: 12, color: '#991b1b', marginBottom: 12,
                }}>
                  <input type="checkbox" checked={confirmSystem} style={{ marginTop: 2 }}
                    onChange={(e) => setConfirmSystem(e.target.checked)} />
                  <span>
                    I understand that <b>{selectedSystem.join(', ')}</b> are system tables — wiping
                    them may delete my own user account and clinic settings, and I may have to
                    re-seed the database before logging in again.
                  </span>
                </label>
              )}

              <label style={{
                fontSize: 10, fontWeight: 700, color: COLORS.gray,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                Type <span style={{ color: COLORS.red }}>{CONFIRM_WORD}</span> to confirm
              </label>
              <input
                value={confirmText}
                autoFocus
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                style={{ ...inputStyle, width: '100%', height: 36, marginTop: 6 }}
              />

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
                <button onClick={() => setConfirmOpen(false)} disabled={running}
                  style={{
                    height: 36, padding: '0 14px', fontSize: 13, borderRadius: 8,
                    border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={runTruncate}
                  disabled={
                    running ||
                    confirmText.trim().toUpperCase() !== CONFIRM_WORD ||
                    (selectedSystem.length > 0 && !confirmSystem)
                  }
                  style={{
                    height: 36, padding: '0 16px', fontSize: 13, fontWeight: 600, borderRadius: 8,
                    border: 'none', color: '#fff',
                    background: (confirmText.trim().toUpperCase() === CONFIRM_WORD &&
                                 (selectedSystem.length === 0 || confirmSystem) && !running)
                      ? COLORS.red : '#cbd5e0',
                    cursor: running ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  {running
                    ? <><span className="spinner-border spinner-border-sm" /> Wiping…</>
                    : <><i className="ti ti-trash" style={{ fontSize: 15 }} /> Wipe now</>}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default DatabaseCleanup
